import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import 'swiper/css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { distanceBetweenTwoPoints } from '../../../utils/utilsDistance';
import { getParkingOrganizationName } from '../../../utils/utilsParking';
import { ParkingType } from '../../../utils/utilTypes';
import { OrganizationTag } from '../OrganizationTag/OrganizationTag';
import { ParkingTypeTag } from '../ParkingTypeTag';
import { CardParkingListItemProps } from './props';
import { AllCardParkingLoadingSkeleton } from './Skeleton';
import { useSessionsSlice } from '../../../slices/useSessionsSlice';

export const CardParkingList = ({
  center,
  parkings,
  isLoading,
  onClick,
}: CardParkingListItemProps) => {
  const { t } = useTranslation();
  const { ongoingSession } = useSessionsSlice();

  if (isLoading) {
    return <AllCardParkingLoadingSkeleton />;
  }

  const sorterParkings = parkings
    .map((x) => ({
      ...x,
      distance: distanceBetweenTwoPoints(center, { lat: x.lat, lng: x.lng }),
    }))
    .filter((p) => {
      return !p.referenceParking;
    })
    .sort((a, b) => a.distance - b.distance);

  return (
    <Swiper
      className="h-full"
      spaceBetween={0}
      slidesPerView={1.8}
      scrollbar={{ draggable: true }}
    >
      {sorterParkings
        .map((parking: ParkingType) => {
          const isParkingInSession =
            ongoingSession?.parking?.id === parking.id ||
            ongoingSession?.parking?.referenceParking?.id === parking.id;

          return {
            ...parking,
            isParkingInSession,
            isDisabled: false,
          };
        })
        .sort(
          (a: ParkingType, b: ParkingType) =>
            Number(b.isParkingInSession) - Number(a.isParkingInSession),
        )
        .map((parking: ParkingType) => {
          const organizationName = getParkingOrganizationName(parking);

          return (
            <SwiperSlide
              key={parking.id}
              style={{ height: '100%' }}
              className={`flex flex-1 min-w-[184px] max-w-[184px] h-full justify-center flex-col px-4 pb-3 ${
                parking.isDisabled && 'cursor-default opacity-50'
              }
                ${parking.isParkingInSession && ' animate-pulse'}  
              `}
              onClick={() => !parking.isDisabled && onClick(parking)}
            >
              <div
                className={`rounded-general shadow-xl h-[100px] mb-3 relative overflow-clip bg-black ${
                  parking.isParkingInSession && 'border-[3px] border-primary '
                }`}
              >
                {parking?.photo && (
                  <>
                    <Image
                      priority
                      className={
                        parking.isParkingInSession
                          ? 'p-1 rounded-none'
                          : 'rounded-general'
                      }
                      src={parking?.photo?.image?.publicUrl}
                      layout="fill"
                      objectFit="fill"
                      alt={parking?.photo?.image?.altText}
                    />

                    <div
                      className={` absolute  top-0 left-0 w-full h-full opacity-40 bg-[#000]`}
                    ></div>
                  </>
                )}
                <div className="absolute top-2 left-2 right-2 flex justify-between items-center">
                  {/* <div className="max-w-[80px]">
                      <ParkingTypeTag type={parking.type} />
                    </div> */}
                  {organizationName && (
                    <div className="max-w-[80px]">
                      <OrganizationTag name={organizationName} />
                    </div>
                  )}
                </div>
                <div className="absolute text-xs font-bold text-white bottom-2 right-3">
                  {parking.capacity - parking.occupied} {t('availableSpots')}
                </div>
              </div>
              <div className="flex items-center min-w-0">
                <div className="text-md font-bold text-black truncate min-w-0">
                  {parking.name}
                </div>
              </div>
              <div className="mt-1 text-xs max-w-[192px] text-gray-dark overflow-hidden whitespace-nowrap overflow-ellipsis">
                {parking.full_address || t('noAddress')}
              </div>
            </SwiperSlide>
          );
        })}
    </Swiper>
  );
};
